import type { FormQuestion, FormResponse } from "../../types/form";
import {
  INVESTOR_ALPHA_BROKERAGE_CHOICES,
  type InvestorAlphaBrokerageChoice,
} from "../../types/investorAlpha";

const normalizeQuestionField = (value: unknown) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

export function getIbkrAccountQuestion(
  questions: FormQuestion[]
): FormQuestion {
  const matchingQuestions = questions.filter(
    (question) =>
      normalizeQuestionField(question.question) === "ibkr account"
      && normalizeQuestionField(question.category) === "ibkr"
      && normalizeQuestionField(question.subcategory) === "ibkr"
  );

  if (matchingQuestions.length !== 1) {
    throw new Error("IBKR account question contract is invalid");
  }

  const question = matchingQuestions[0];
  if (!Number.isInteger(question.id) || question.id <= 0) {
    throw new Error("IBKR account question ID is invalid");
  }
  if (normalizeQuestionField(question.type) !== "select") {
    throw new Error("IBKR account question must be a select");
  }

  let options: unknown;
  try {
    options = JSON.parse(question.options);
  } catch {
    throw new Error("IBKR account options are invalid");
  }

  if (
    !Array.isArray(options)
    || options.length !== INVESTOR_ALPHA_BROKERAGE_CHOICES.length
    || options.some((option) => typeof option !== "string")
    || INVESTOR_ALPHA_BROKERAGE_CHOICES.some((option) => !options.includes(option))
  ) {
    throw new Error("IBKR account options do not match the supported choices");
  }

  return question;
}

export function getSavedIbkrAccountChoice(
  question: FormQuestion,
  responses: FormResponse[]
): InvestorAlphaBrokerageChoice | null {
  if (responses.length === 0) return null;
  if (responses.length !== 1 || responses[0].questionId !== question.id) {
    throw new Error("Saved IBKR account response contract is invalid");
  }

  const answer = responses[0].answer;
  if (!INVESTOR_ALPHA_BROKERAGE_CHOICES.some((choice) => choice === answer)) {
    throw new Error("Saved IBKR account answer is not supported");
  }

  return answer as InvestorAlphaBrokerageChoice;
}
